import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import TodayOrder from './TodayOrder';
import OrderHistory from './OrderHistory';
import DeliveryAgent from './DeliveryAgent';
import { isSupermarketContext, resolveEmployeeStoreContext } from './entityContext';
import { DEFAULT_DELIVERY_AGENT_ORDER_SOURCE, ORDER_CHANNELS } from './menuChannels';
import {
  filterOrderChannelsByServices,
  pickAllowedOrderChannel,
} from './restaurantServiceAccess';
import StoreOrders from './StoreOrders';
import OrdersServiceChannelSelect from './OrdersServiceChannelSelect';

import './css/Order.css';
import './css/TodayOrder.css';

function normalizeCateringViewParam(raw) {
  if (!raw) return 'orders';
  const v = String(raw).trim().toLowerCase();
  if (v === 'quotes') return 'quotes';
  if (v === 'orderhistory' || v === 'history') return 'orderHistory';
  return 'orders';
}

function normalizeOrdersViewParam(raw) {
  if (!raw) return 'orders';
  const v = String(raw).trim().toLowerCase();
  if (v === 'orderhistory' || v === 'history' || v === 'delivered') return 'orderHistory';
  return 'orders';
}

function normalizePartyHallViewParam(raw) {
  if (!raw) return 'quotes';
  const v = String(raw).trim().toLowerCase();
  if (v === 'orders' || v === 'bookings') return 'orders';
  return 'quotes';
}

function normalizeOrderTypeFromUrl(raw) {
  const fromUrl = String(raw || '').trim().toLowerCase();
  if (fromUrl === 'online') return 'myeats';
  if (fromUrl === 'partyhall' || fromUrl === 'party-hall' || fromUrl === 'party_hall') {
    return 'partyHall';
  }
  return fromUrl;
}

const Order = ({
  userId,
  deliveryOrderSource: deliveryOrderSourceProp,
  enabledServiceTypes = null,
  servicesLoading = false,
}) => {
  const serviceFilterOptions = { servicesLoading };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleSelectOrderChannel = useCallback(
    (channelKey) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'orders');
      next.set('orderType', channelKey);
      next.delete('status');
      if (channelKey !== 'catering') next.delete('cateringView');
      if (channelKey !== 'partyHall') next.delete('partyHallView');
      if (channelKey !== 'offline') next.delete('offlineView');
      if (channelKey !== 'myeats' && channelKey !== 'jreats' && channelKey !== 'online') {
        next.delete('onlineView');
      }
      navigate({ search: `?${next.toString()}` });
    },
    [navigate, searchParams]
  );

  const initialOrderType = (() => {
    const fromUrl = normalizeOrderTypeFromUrl(searchParams.get('orderType'));
    if (isSupermarketContext()) {
      if (fromUrl === 'pos' || fromUrl === 'myeats' || fromUrl === 'restaurant') {
        return fromUrl;
      }
      return 'pos';
    }
    const allowed = filterOrderChannelsByServices(
      ORDER_CHANNELS,
      enabledServiceTypes,
      serviceFilterOptions
    );
    const allowedKeys = allowed.map((channel) => channel.key);
    if (
      fromUrl === 'myeats' ||
      fromUrl === 'jreats' ||
      fromUrl === 'catering' ||
      fromUrl === 'partyHall' ||
      fromUrl === 'offline'
    ) {
      if (servicesLoading) {
        return 'offline';
      }
      if (!enabledServiceTypes || allowedKeys.includes(fromUrl)) {
        return fromUrl;
      }
    }
    return (
      pickAllowedOrderChannel('offline', enabledServiceTypes, serviceFilterOptions) || 'offline'
    );
  })();

  const [deliveryOrderSource, setDeliveryOrderSource] = useState(
    deliveryOrderSourceProp ?? DEFAULT_DELIVERY_AGENT_ORDER_SOURCE
  );
  const [orderTypeFilter, setOrderTypeFilter] = useState(initialOrderType);
  const [offlineOrderType, setOfflineOrderType] = useState(() =>
    normalizeOrdersViewParam(searchParams.get('offlineView'))
  );
  const [onlineOrderType, setOnlineOrderType] = useState(() =>
    normalizeOrdersViewParam(searchParams.get('onlineView'))
  );
  const [cateringOrderType, setCateringOrderType] = useState(() =>
    normalizeCateringViewParam(searchParams.get('cateringView'))
  );
  const [partyHallOrderType, setPartyHallOrderType] = useState(() =>
    normalizePartyHallViewParam(searchParams.get('partyHallView'))
  );

  useEffect(() => {
    const mapped = normalizeOrderTypeFromUrl(searchParams.get('orderType'));
    if (isSupermarketContext()) {
      const validTypes = ['pos', 'myeats', 'restaurant'];
      if (validTypes.includes(mapped) && mapped !== orderTypeFilter) {
        setOrderTypeFilter(mapped);
      }
      return;
    }

    const allowed = filterOrderChannelsByServices(
      ORDER_CHANNELS,
      enabledServiceTypes,
      serviceFilterOptions
    );
    const allowedKeys = allowed.map((channel) => channel.key);
    if (allowedKeys.includes(mapped) && mapped !== orderTypeFilter) {
      setOrderTypeFilter(mapped);
      return;
    }
    const fallback = pickAllowedOrderChannel(
      orderTypeFilter,
      enabledServiceTypes,
      serviceFilterOptions
    );
    if (fallback && fallback !== orderTypeFilter) {
      setOrderTypeFilter(fallback);
    }
  }, [searchParams, orderTypeFilter, enabledServiceTypes, servicesLoading]);

  useEffect(() => {
    if (orderTypeFilter === 'offline') {
      setOfflineOrderType(normalizeOrdersViewParam(searchParams.get('offlineView')));
    }
    if (
      orderTypeFilter === 'myeats' ||
      orderTypeFilter === 'jreats' ||
      orderTypeFilter === 'online'
    ) {
      setOnlineOrderType(normalizeOrdersViewParam(searchParams.get('onlineView')));
    }
    if (orderTypeFilter === 'catering') {
      setCateringOrderType(normalizeCateringViewParam(searchParams.get('cateringView')));
    }
    if (orderTypeFilter === 'partyHall') {
      setPartyHallOrderType(normalizePartyHallViewParam(searchParams.get('partyHallView')));
    }
  }, [searchParams, orderTypeFilter]);

  useEffect(() => {
    if (deliveryOrderSourceProp != null) {
      setDeliveryOrderSource(deliveryOrderSourceProp);
    }
  }, [deliveryOrderSourceProp]);

  const serviceChannelSelect = (
    <OrdersServiceChannelSelect
      value={orderTypeFilter}
      onChange={handleSelectOrderChannel}
      enabledServiceTypes={enabledServiceTypes}
      servicesLoading={servicesLoading}
      isStore={isSupermarketContext()}
    />
  );

  const user = JSON.parse(localStorage.getItem('user'));
  const userRole = user?.userRole || user?.role;
  const isDeliveryAgent = userRole === 'DELIVERY_AGENT';

  if (isDeliveryAgent) {
    const isStore = resolveEmployeeStoreContext(user);
    return (
      <div className="order-container">
        <div className="sub-tab-content" style={{ backgroundColor: '#ffffff' }}>
          <DeliveryAgent
            orderSource={deliveryOrderSourceProp ?? deliveryOrderSource}
            isStore={isStore}
          />
        </div>
      </div>
    );
  }

  if (isSupermarketContext()) {
    return (
      <div className="order-container">
        <div className="sub-tab-content" style={{ backgroundColor: '#ffffff' }}>
          <StoreOrders
            orderTypeFilter={orderTypeFilter}
            initialActiveStatus={searchParams.get('status') || ''}
            serviceChannelSelect={serviceChannelSelect}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="order-container">
      <div className="sub-tab-content" style={{ backgroundColor: '#ffffff' }}>
        <div>
          <TodayOrder
            userId={userId}
            orderTypeFilter={orderTypeFilter}
            offlineOrderType={offlineOrderType}
            cateringOrderType={cateringOrderType}
            partyHallOrderType={partyHallOrderType}
            onlineOrderType={onlineOrderType}
            setOnlineOrderType={setOnlineOrderType}
            initialActiveStatus={searchParams.get('status') || ''}
            serviceChannelSelect={serviceChannelSelect}
          />
        </div>
      </div>
    </div>
  );
};

export default Order;
